import { useEffect, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Button, Container, Divider, Typography, FormControl, ToggleButtonGroup, ToggleButton, Tooltip, CircularProgress } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";

import ResetIcon from '@mui/icons-material/SettingsBackupRestore'; 
import { useDispatch, useSelector } from "react-redux";

import { ExpandMore, PlayArrow } from "@mui/icons-material";

import { selectOptions } from "../../reducers/optionsSlice";
import { selectFBA, setFBASettings, updateFBA } from "../../reducers/fbaSlice";
import { FBASetting } from "./FBASetting";

import { useMemo } from "react";
import { useSocket } from "../../context/useSocket";
import { selectUser } from "../../reducers/userSlice";
import { handleCalculateFBAClick } from "../../containers/FBAContainer";
// import { defaultSettings } from "./fbaDefaultSettings";


const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: 'transparent',
    width: '100%',
    height: 'calc(100vh)',
    overflowY: 'auto',
    padding: 0,
    paddingBottom: 140
  },
  header: {
    margin: 10
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0 10px',
    marginBottom: 20,
  },
  toggleButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 10
  },
  cutoff: {
    padding: '0 10px'
  },
  emptyForecast: {
    height: 'calc(100% - 120px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  details: {
    margin: 0,
    padding: 0
  },
  summary: {
    margin: 0
  },
  settings: {
    marginTop: 0
  },
  heading: {
    fontWeight: 'bold'
  }
}));

export const FBASettings = ({ fbaSettings, setFbaSettings, fbaTabItem, darkMode, fbaUnits, setFBAUnits }) => {

  const classes = useStyles();
  const theme = useTheme();

  const { socket } = useSocket();

  const dispatch = useDispatch();
  const options = useSelector(selectOptions);
  const user = useSelector(selectUser);
  const fba = useSelector(selectFBA);
  const currentFBA = fba?.byId?.[fba?.currentFBAId];

  const reservoirType = fbaSettings?.type?.value;

  const groupingQuicklook = useMemo(() => {

    switch(fbaTabItem) {
      case 0: // Input Data Settings Grouping
        return ([
          {
            name: 'General Settings',
            parameters: ['type', 't_r']
          },
          {
            name: 'Gas Properties',
            parameters: 
            reservoirType === 1 ? // tight oil
            ['g']
            :
            ['g', 'tSep', 'pSep']
          },
          reservoirType === 1 ? // tight oil
          {
            name: 'Oil Properties',
            parameters: ['gor', 'gamma']
          }
          :
          {
            name: 'Condensate Properties',
            parameters: ['cgr', 'condensateApi']
          },
          reservoirType === 1 ? 
          {
            name: 'Fracture Properties',
            parameters: ['p_fi', 's_w', 'n_f', 'loadFluidPumped']
          } :
          {
            name: 'Fracture Properties',
            parameters: ['p_fi', 's_w', 'n_f', 'loadFluidPumped']
          },
          {
            name: 'Reservoir Properties',
            parameters: ['h', 'phi_m']
          },
          {
            name: 'Wellbore Settings',
            parameters: ['r_w']
          }
        ]);
      case 1: // Wellbore Model Settings Grouping
        return ([
          {
            name: 'Wellbore Model Settings',
            parameters: [
              'pressureMethod', 
              'id', 
              'wht', 
              'midPointPerfs'
            ]
          }
        ]);
      case 2: // Diagnostic Plots Settings Grouping
        return ([
          {
            name: 'Linear Flow Fit',
            parameters: ['linearFlowFitIntercept', 'linearFlowFitInterceptI', 'linearFlowEndTmb']
          },
          {
            name: 'Fracture Depletion Fit',
            parameters: ['fractureDepletionFitIntercept', 'fractureDepletionFitInterceptI']
          },
          {
            name: 'Multi-Phase Depletion Fit',
            parameters: ['multiPhaseFitIntercept']
          },
        ]);
      case 3: // RTA Settings Grouping
        return ([
          {
            name: 'Square Root of Time',
            parameters: ['sqrtTimeFitSlope', 'sqrtTimeFitIntercept']
          },
          {
            name: 'Flowing Material Balance',
            parameters: ['fmbFitSlope', 'fmbFitIntercept']
          },
          {
            name: 'Linear Flow Type Curve',
            parameters: ['fractureConductivityLFTC']
          },
        ]);
      case 4: // History Match Settings Grouping
        return ([
          {
            name: 'Frac. Properties BBT (SP)',
            parameters: ['p_fi', 's_w', 'fractureConductivityPreBT','skinPreBT','drainageAreaPreBT']
          },
          {
            name: 'Frac. Properties ABT (MP)',
            parameters: 
              reservoirType === 1 ? 
              [
                'skinPostBT', 
                'drainageAreaPostBT', 
                'kroPostBT', 
                'nPrimePostBT', 
                'mPrimePostBT'
              ]
              :
              [
                'skinPostBT', 
                'drainageAreaPostBT', 
                'krgPostBT', 
                'nPrimePostBTGas', 
                'mPrimePostBT'
              ]
          },
          {
            name: 'Matrix Inflow',
            parameters: 
            reservoirType === 1 ? 
            [
              'pbtMatrixInflow', 
              'formationCompressibility', 
              'mobileOilSaturation', 
              'kMatrixInflow', 
              'skinMatrixInflow', 
              'fractureSpacingMatrixInflow',
              'kroMatrixInflow',
              'nPrimeMatrixInflow',
              'mPrimeMatrixInflow'
            ]
            :
            [
              'pbtMatrixInflow', 
              'formationCompressibility', 
              'mobileGasSaturation', 
              'kMatrixInflow', 
              'skinMatrixInflow', 
              'fractureSpacingMatrixInflow',
              'krgMatrixInflow',
              'nPrimeMatrixInflowGas',
              'mPrimeMatrixInflow'
            ]
          },
        ]);
      default:
        return [];
    };

  }, [reservoirType, fbaTabItem]);

  const groupingAdvanced = useMemo(() => {

    switch(fbaTabItem) {
      case 0: // Input Data Settings Grouping
        return ([
          {
            name: 'General Settings',
            parameters: ['type', 't_r', 'c_f']
          },
          {
            name: 'Gas Properties',
            parameters: 
            reservoirType === 1 ?
            ['g', 'n2', 'co2', 'h2s']
            :
            ['g', 'n2', 'co2', 'h2s', 'tSep', 'pSep']
          },
          reservoirType === 1 ?
          {
            name: 'Oil Properties',
            parameters: ['gor', 'gamma']
          }
          :
          {
            name: 'Condensate Properties',
            parameters: ['cgr', 'condensateApi']
          },
          reservoirType === 1 ?
          {
            name: 'Fracture Properties',
            parameters: ['p_fi', 's_w', 'n_f', 'phi_f', 'k_phi_exp', 'w_f', 'loadFluidPumped', 'fracsAttempted']
          } :
          {
            name: 'Fracture Properties',
            parameters: ['p_fi', 's_w', 'n_f', 'phi_f', 'k_phi_exp', 'w_f', 'loadFluidPumped', 'fracsAttempted']
          },
          {
            name: 'Reservoir Properties',
            parameters: ['h', 'phi_m']
          },
          {
            name: 'Wellbore Settings',
            parameters: ['r_w']
          },
          // {
          //   name: 'Model Settings',
          //   parameters: ['c_phi']
          // },
        ]);
      case 1: // Wellbore Model Settings Grouping
        return ([
          {
            name: 'Wellbore Model Settings',
            parameters: [
              'pressureMethod', 
              'id', 
              'geothermalGradient', 
              'wht', 
              'midPointPerfs'
            ]
          }
        ]);
      case 2: // Diagnostic Plots Settings Grouping
        return ([
          {
            name: 'Derivative Settings',
            parameters: ['derivativeWindowRNP', 'derivativeWindowIRNP']
          },
          {
            name: 'Linear Flow Fit',
            parameters: ['linearFlowFitIntercept', 'linearFlowFitInterceptI', 'linearFlowEndTmb']
          },
          {
            name: 'Fracture Depletion Fit',
            parameters: ['fractureDepletionFitIntercept', 'fractureDepletionFitInterceptI']
          },
          {
            name: 'Multi-Phase Depletion Fit',
            parameters: ['multiPhaseFitIntercept']
          },
        ]);
      case 3: // RTA Settings Grouping
        return ([
          {
            name: 'Square Root of Time',
            parameters: ['sqrtTimeFitSlope', 'sqrtTimeFitIntercept']
          },
          {
            name: 'Flowing Material Balance',
            parameters: ['fmbFitSlope', 'fmbFitIntercept']
          },
          {
            name: 'Linear Flow Type Curve',
            parameters: ['fractureConductivityLFTC']
          },
        ]);
      case 4: // History Match Settings Grouping
        return ([
          {
            name: 'Frac. Properties BBT (SP)',
            parameters: ['p_fi', 's_w', 'fractureConductivityPreBT','k_phi_exp','skinPreBT','drainageAreaPreBT']
          },
          {
            name: 'Frac. Properties ABT (MP)',
            parameters: 
              reservoirType === 1 ? 
              [
                'skinPostBT', 
                'drainageAreaPostBT', 
                'kroPostBT', 
                'nPrimePostBT', 
                'mPrimePostBT'
              ]
              :
              [
                'skinPostBT', 
                'drainageAreaPostBT', 
                'krgPostBT', 
                'nPrimePostBTGas', 
                'mPrimePostBT'
              ]
          },
          {
            name: 'Matrix Inflow',
            parameters: 
            reservoirType === 1 ? 
            [
              'pbtMatrixInflow', 
              'formationCompressibility', 
              'mobileOilSaturation', 
              'kMatrixInflow', 
              'skinMatrixInflow', 
              'fractureSpacingMatrixInflow',
              'kroMatrixInflow',
              'nPrimeMatrixInflow',
              'mPrimeMatrixInflow'
            ]
            :
            [
              'pbtMatrixInflow', 
              'formationCompressibility', 
              'mobileGasSaturation', 
              'kMatrixInflow', 
              'skinMatrixInflow', 
              'fractureSpacingMatrixInflow',
              'krgMatrixInflow',
              'nPrimeMatrixInflowGas',
              'mPrimeMatrixInflow'
            ]
          },
        ]);
      default:
        return [];
    };

  }, [reservoirType, fbaTabItem]);


  const grouping = (fbaSettings?.analysisType?.value === 1 ? groupingQuicklook : groupingAdvanced);

  const [expandAll, setExpandAll] = useState(true);
  const [expanded, setExpanded] = useState(grouping.map(g => g.name));
  
  useEffect(() => {
    if (expandAll) setExpanded(grouping.map(g => g.name));
  }, [grouping, expandAll]);

  useEffect(() => {
    if (currentFBA?.settings) setFbaSettings(currentFBA?.settings);
  }, [currentFBA?.settings, setFbaSettings]);


  try {
    return (
      <Container className={classes.root} style={{color: "primary", overflow: 'auto'}}>
        <div style={{position: 'fixed', zIndex: 69, backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'], width: '299px'}}>
          <div className={classes.header}>
            <Typography variant="subtitle2" color="textSecondary" noWrap>
              {currentFBA?.name || ""}
            </Typography>
            <Typography variant="h6">
              {getHeadingName(options?.fbaTabItem)}
            </Typography>
          </div>

          <div className={classes.buttons}>
            <Tooltip title="Reset FBA settings to last saved version">
              <Button 
                variant="outlined" 
                color={(darkMode ? "inherit" : "primary")}
                style={{color: (darkMode ? '#fff' : theme.palette.primary.main), borderColor: (darkMode ? '#fff' : theme.palette.primary.main)}}
                startIcon={<ResetIcon />} 
                className={classes.button} 
                size='small' 
                onClick={() => handleResetFBAClick(currentFBA)}
              >
                Reset
              </Button>
            </Tooltip>

            <div style={{position: 'relative'}}>
              <Tooltip title="Calculate FBA (shortcut F9)">
                <Button 
                  variant="contained" 
                  color={(darkMode ? "inherit" : "primary")}
                  style={{color: (darkMode ? '#000' : '#fff')}}
                  startIcon={<PlayArrow />} 
                  className={classes.button} 
                  size='small'
                  onClick={() => handleCalculateFBAClick(currentFBA, fbaSettings, socket, user?.value?.email, dispatch)}
                  disabled={options?.isLoadingData}
                >
                  Calculate
                </Button>
              </Tooltip>
              {options?.isLoadingData && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-11px',
                    marginLeft: '40px',
                  }}
                />
              )}
            </div>
            
          </div>

          {(fbaTabItem === 5 || fbaTabItem === 6) &&  
          <FormControl component="fieldset" style={{width: '100%', padding: '0 10px', marginTop: 0}}>
            <Typography>Units</Typography>
            <ToggleButtonGroup
              value={fbaUnits}
              exclusive
              onChange={handleFbaUnitsChange}
              size="small"
              style={{height: 30, width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 10}}
            >
              <ToggleButton value="metric" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  Metric
                </Typography>
              </ToggleButton>
              <ToggleButton value="field" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  Field
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>}

          {fbaTabItem !== 5 && fbaTabItem !== 6 &&
          <FormControl component="fieldset" style={{width: '100%', padding: '0 10px', marginTop: 0}}>
            <Typography>Analysis Type</Typography>
            <ToggleButtonGroup
              value={fbaSettings?.analysisType?.value}
              exclusive
              onChange={handleAnalysisTypeChange}
              size="small"
              style={{height: 30, width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 10}}
            >
              <ToggleButton value={1} size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  Quicklook
                </Typography>
              </ToggleButton>
              <ToggleButton value={2} size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  Advanced
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>}

          {fbaTabItem !== 5 && fbaTabItem !== 6 && <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
            <Button size="small" onClick={handleExpandAll}><Typography fontSize={10}>{expandAll ? 'Collapse' : 'Expand'} All</Typography></Button>
          </div>}
          
          <Divider />
        </div>

        
        {fbaTabItem !== 5 && fbaTabItem !== 6 && <div style={{position: 'absolute', top: 260, width: '299px', height: 'calc(100% - 260px)', overflowY: 'auto'}}>
          {grouping?.map(group => {
            return (
              <div className={classes.settings} key={group?.name} >
                <Accordion 
                  disableGutters={false}
                  expanded={expanded.indexOf(group?.name) !== -1}
                  elevation={0}
                  style={{backgroundColor: theme.palette?.elevationOne?.[darkMode ? 'dark' : 'light'], borderRadius: 0}}>
                  <AccordionSummary expandIcon={<ExpandMore />} onClick={() => handleExpand(group?.name)}>
                    <Tooltip title={getGroupingName(group?.name)}><Typography className={classes.heading} noWrap>{group?.name || ""}</Typography></Tooltip>
                  </AccordionSummary>
                  <AccordionDetails className={classes.details}>
                    {group?.parameters?.map(param => {
                      return (
                        <div key={param}>
                          <FBASetting parameter={param} fbaSettings={fbaSettings} setFbaSettings={setFbaSettings} darkMode={darkMode} />
                          <Divider style={{width: '100%'}}/>
                        </div>
                      )
                    })}
                  </AccordionDetails>
                </Accordion>
                <Divider />
              </div>    
            );
          })}
        </div>}

      </Container>
    );
  
    // FUNCTIONS
  
    // async function handleCalculateFBAClick(fba) {
    //   try {
    //     const newFba = {...fba};
    //     const newSettings = JSON.parse(JSON.stringify({
    //       ...fbaSettings, 
    //       multiPhaseFitIntercept: fba?.settings?.multiPhaseFitIntercept, 
    //       linearFlowFitIntercept: fba?.settings?.linearFlowFitIntercept,
    //       fractureDepletionFitIntercept: fba?.settings?.fractureDepletionFitIntercept,
    //       linearFlowEndTmb: fba?.settings?.linearFlowEndTmb,
    //       sqrtTimeFitSlope: fba?.settings?.sqrtTimeFitSlope,
    //       sqrtTimeFitIntercept: fba?.settings?.sqrtTimeFitIntercept,
    //       fmbFitSlope: fba?.settings?.fmbFitSlope,
    //       fmbFitIntercept: fba?.settings?.fmbFitIntercept
    //     }));


    //     // calculate solids wt%
    //     const solids_bbt = calculateWtPercentSolids('bbt', newFba?.fbaData);
    //     const solids_abt = calculateWtPercentSolids('abt', newFba?.fbaData);
    //     newSettings.solids_bbt.value = solids_bbt;
    //     newSettings.solids_abt.value = solids_abt;
    //     newSettings.wtPercentSolidsMatrixInflow.value = solids_abt;


    //     // calculate sgWater
    //     const averageSolids = (solids_bbt + solids_abt) / 2;
    //     const bW = 1;
    //     const sgWater = ((62.368 + 47 * averageSolids) / bW) / 62.368;
    //     newSettings.sgWater.value = sgWater;

    //     // calculate s_o
    //     const s_o = 1 - newSettings?.s_w?.value;
    //     newSettings.s_o.value = s_o;


    //     // calculate rB
    //     const rB = reservoirType === 1 ? newSettings?.gor?.value :  (1 / (newSettings?.cgr?.value / 1e6));
    //     newSettings.rB.value = rB;


    //     newFba.settings = newSettings;

    //     if (socket && socket.readyState === 1) {
    //       socket.send(JSON.stringify({messageType: 'fba', payload: newFba, userEmail: user?.value?.email}));
    //     };

    //     dispatch(setIsLoadingData(true));
    //   } catch (error) {
    //     console.log(error);
    //     return;
    //   };
    // };

    // /**
    //  * Calculates wt% solids before or after breakthrouh
    //  *
    //  * @param {string} type - Type of calculation ('bbt' or 'abt').
    //  * @param {Array<{}>} fbaData - FBA input data array.
    //  * @returns {number} - Weight percent solids as a decimal.
    //  * @throws {Error} - Throws an error if either the type is wrong of fbaData is missing.
    // */
    // function calculateWtPercentSolids(type, fbaData) {
    //   try {

    //     if (!['bbt', 'abt']?.includes(type)) throw new Error('Type must be either bbt or abt');
    //     if (!fbaData || !fbaData?.length > 0) throw new Error('FBA input data is required');

    //     let salinity = null;
    //     const defaultSalinity = type === 'bbt' ? 0.05 : 0.1;

    //     if (type === 'bbt') { // calculate BBT wt% solids
    //       // determine if single phase is present
    //       let singlePhaseIndex = 0;

    //       for (let i = 0; i < fbaData?.length; i++) {
    //         const {water, oil, gas, condensate} = fbaData[i];
    //         if (water > 1 && ((oil || 0) + (gas || 0) + (condensate || 0)) < 1) {
    //           singlePhaseIndex = i;
    //         } else {
    //           break;
    //         };
    //       };

    //       if (singlePhaseIndex > 11) {
    //         const dataSlice = fbaData?.slice(0, singlePhaseIndex + 1);
    //         salinity = dataSlice?.reduce((tot, curr) => curr.salinity / 10000 + tot, 0) / dataSlice?.length / 100;
    //       } else {
    //         const dataSlice = fbaData?.slice(0, 10);
    //         salinity = dataSlice?.reduce((tot, curr) => curr.salinity / 10000 + tot, 0) / dataSlice?.length / 100;
    //       };

          
    //     } else if (type === 'abt') { // calculate ABT wt% solids
    //       salinity = Math.max(...fbaData?.map(d => d.salinity)) / 10000 / 100 * 1.2;
    //     };

    //     if (!isNaN(salinity) && salinity > 0 && isFinite(salinity)) {
    //       return salinity;
    //     } else {
    //       return defaultSalinity;
    //     };

    //   } catch(error) {
    //     console.log(error);
    //     throw new Error('Error calculating weight percent solids');
    //   };
    // };

    async function handleResetFBAClick(fba) {
      try {
        setFbaSettings(() => {return {...fba.lastSavedSettings, units: fbaSettings.units}});
        dispatch(setFBASettings({ fbaId: fba.id, settings: {...fba?.lastSavedSettings, units: fbaSettings.units} }));
      } catch (error) {
        console.log(error);
        return;
      };
    };

    function handleFbaUnitsChange(e, value) {
      try {
        if (!value) return;
        
        // let newFba = {...currentFBA};
        // let newSettings = {...fbaSettings};
        // newSettings.units = {...newSettings.units, value: value}
        // newFba.settings = newSettings;
        // setFbaSettings(newSettings);
        // dispatch(updateFBA({fbaId: newFba?.id, fba: newFba}));
        setFBAUnits(value);
      } catch (error) {
        console.log(error);
        return;
      };
    };

    function handleAnalysisTypeChange(e, value) {
      try {
        if (!value) return;
        
        let newFba = {...currentFBA};
        let newSettings = JSON.parse(JSON.stringify(fbaSettings));
        newSettings.analysisType.value = value;
        newFba.settings = newSettings;
        setFbaSettings(newSettings);
        dispatch(updateFBA({fbaId: newFba?.id, fba: newFba}));
      } catch (error) {
        console.log(error);
        return;
      };
    };

    function getHeadingName(fbaTabItem) {
      switch(fbaTabItem) {
        case 0:
          return 'Input Data Settings';
        case 1:
          return 'Wellbore Model Settings';
        case 2:
          return 'Diagnostic Plots Settings';
        case 3:
          return 'RTA Settings';
        case 4:
          return 'History Match Settings';
        case 5:
          return 'Analysis Settings';
        default:
          return '';
      };
    };

    function getGroupingName(name) {
      switch(name) {
        case 'Frac. Properties BBT (SP)':
          return 'Fracture Properties Before Breakthrough (Single Phase)';
        case 'Frac. Properties ABT (MP)':
          return 'Fracture Properties After Breakthrough (Multi Phase)';
        default:
          return name;
      }
    };

    function handleExpand(name) {
      let newExpanded = [...expanded];
      const indexOf = expanded.indexOf(name);
      if (indexOf !== -1) {
        newExpanded.splice(indexOf, 1);
        setExpanded(newExpanded);
      } else {
        newExpanded.push(name);
        setExpanded(newExpanded);
      };
    };

    function handleExpandAll() {
      if (expandAll) {
        setExpanded([]);
        setExpandAll(false);
      } else {
        setExpanded(() => grouping.map(g => g.name));
        setExpandAll(true);
      };
    };

  } catch (error) {
    console.log(error);
    return (<></>);
  };
};